import * as github from '@actions/github';
import * as core from '@actions/core';
import * as fs from 'node:fs';
import * as path from 'node:path';
import { ByteBudget, snapshotBudgetCharge } from './byte-budget';
import { ExternalSourceRule } from './external-source-rules';
import { looksLikeText, safeSnapshotPath } from './utils';

type Octokit = ReturnType<typeof github.getOctokit>;

interface ChangedFile {
  filename?: string | null;
  patch?: string | null;
}

interface GitTreeEntry {
  type?: string;
  path?: string;
  sha?: string;
  size?: number;
}

const MAX_EXTERNAL_SKIP_DETAILS = 200;

export async function prepareExternalSources({
  octokit,
  coreLogger,
  baseRepository,
  workspaceDir,
  externalSourcesDir,
  externalSourcesDirResolved,
  changedFiles,
  changedFileTexts,
  rules,
  maxSnapshotBytes,
  snapshotBudget,
  reviewBundlePath,
}: {
  octokit: Octokit;
  coreLogger: typeof core;
  baseRepository: string;
  workspaceDir: string;
  externalSourcesDir: string;
  externalSourcesDirResolved: string;
  changedFiles: ChangedFile[];
  changedFileTexts: Map<string, string>;
  rules: ExternalSourceRule[];
  maxSnapshotBytes: number;
  snapshotBudget: ByteBudget;
  reviewBundlePath: string;
}): Promise<string[]> {
  const activeRules: ExternalSourceRule[] = [];
  const manifest = ['# External Source Snapshots', '', `Base repository: ${baseRepository}`, ''];
  let detectionFailed = false;
  for (const rule of rules) {
    try {
      if (isExternalDependencyPR(rule, changedFiles, changedFileTexts, workspaceDir)) {
        activeRules.push(rule);
      }
    } catch (error) {
      detectionFailed = true;
      const message = error instanceof Error ? error.message : String(error);
      coreLogger.warning(`External dependency detection unavailable for ${rule.owner}/${rule.repo}: ${message}`);
      manifest.push(...unavailableExternalSourceSection(rule, message), '');
    }
  }
  if (activeRules.length === 0 && !detectionFailed) {
    return [];
  }
  for (const rule of activeRules) {
    let section: string[];
    try {
      section = await prepareExternalSource({
        rule,
        octokit,
        workspaceDir,
        externalSourcesDir,
        externalSourcesDirResolved,
        changedFileTexts,
        maxSnapshotBytes,
        snapshotBudget,
        reviewBundlePath,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      coreLogger.warning(`External source snapshot unavailable for ${rule.owner}/${rule.repo}: ${message}`);
      section = unavailableExternalSourceSection(rule, message);
    }
    manifest.push(...section, '');
  }
  return manifest;
}

export function isExternalDependencyPR(
  rule: ExternalSourceRule,
  changedFiles: ChangedFile[],
  changedFileTexts: Map<string, string>,
  workspaceDir: string,
): boolean {
  const watchedFiles = externalWatchedFiles(rule, changedFileTexts);
  const moduleLocked = hasExternalModuleLock({ rule, workspaceDir, changedFileTexts });
  return changedFiles.some((file) => {
    const filename = String(file.filename || '');
    if (rule.surfacePrefixes.some((prefix) => pathMatchesPrefix(filename, prefix)) && moduleLocked) {
      return true;
    }
    if (!watchedFiles.has(filename) && !rule.markerPrefixes.some((prefix) => pathMatchesPrefix(filename, prefix))) {
      return false;
    }
    const text = changedFileTexts.get(filename) || '';
    if (watchedFiles.has(filename)) {
      return changedFilePatchHasExternalDependencyMarker(rule, file, text);
    }
    return hasExternalDependencyMarker(rule, text);
  });
}

async function prepareExternalSource({
  rule,
  octokit,
  workspaceDir,
  externalSourcesDir,
  externalSourcesDirResolved,
  changedFileTexts,
  maxSnapshotBytes,
  snapshotBudget,
  reviewBundlePath,
}: {
  rule: ExternalSourceRule;
  octokit: Octokit;
  workspaceDir: string;
  externalSourcesDir: string;
  externalSourcesDirResolved: string;
  changedFileTexts: Map<string, string>;
  maxSnapshotBytes: number;
  snapshotBudget: ByteBudget;
  reviewBundlePath: string;
}): Promise<string[]> {
  let resolution = await resolveExternalRevision({
    rule,
    octokit,
    workspaceDir,
    changedFileTexts,
  });
  if (resolution.kind === 'local-replace') {
    const baseResolution = await resolveExternalRevisionFromWorkspaceBase({
      rule,
      octokit,
      workspaceDir,
    });
    if (baseResolution.kind === 'external') {
      resolution = baseResolution;
    } else if (baseResolution.kind === 'local-replace') {
      return [];
    } else {
      throw new Error(`Could not resolve base ${rule.module} revision for ${rule.name} external dependency review inputs.`);
    }
  }
  if (resolution.kind !== 'external') {
    throw new Error(`Could not resolve ${rule.module} revision for ${rule.name} external dependency review inputs.`);
  }

  const { data: commit } = await octokit.rest.repos.getCommit({
    owner: rule.owner,
    repo: rule.repo,
    ref: resolution.sha,
  });
  const { data: tree } = await octokit.rest.git.getTree({
    owner: rule.owner,
    repo: rule.repo,
    tree_sha: commit.commit.tree.sha,
    recursive: '1',
  });
  if (tree.truncated) {
    throw new Error(`GitHub returned a truncated recursive tree for ${rule.owner}/${rule.repo}@${commit.sha}.`);
  }

  const sourceRoot = path.join(externalSourcesDir, rule.repo);
  const sourceRootResolved = path.resolve(sourceRoot);
  const manifest = [
    `## ${rule.owner}/${rule.repo}`,
    `- Commit: ${commit.sha}`,
    `- Reason: ${rule.reason} Review must see the external source at the pinned revision \`${rule.module}\`.`,
    '',
  ];

  let written = 0;
  let budgetSkippedFiles = 0;
  let detailedSkips = 0;
  let omittedSkipDetails = 0;
  const noteSkip = (message: string): void => {
    if (detailedSkips < MAX_EXTERNAL_SKIP_DETAILS) {
      manifest.push(message);
      detailedSkips += 1;
    } else {
      omittedSkipDetails += 1;
    }
  };
  const treeEntries = tree.tree as GitTreeEntry[];
  for (const entry of treeEntries
    .filter((item) => item.type === 'blob')
    .sort((left, right) => String(left.path || '').localeCompare(String(right.path || '')))) {
    if (!entry.path || !entry.sha) {
      continue;
    }
    if (entry.size && entry.size > maxSnapshotBytes) {
      noteSkip(`- Skipped oversized file: ${entry.path} (${entry.size} bytes)`);
      continue;
    }
    if (
      typeof entry.size === 'number'
      && snapshotBudgetCharge(entry.size) > snapshotBudget.remainingBytes
    ) {
      budgetSkippedFiles += 1;
      continue;
    }

    const { normalized, target } = safeSnapshotPath({
      filename: entry.path,
      rootDir: sourceRoot,
      rootDirResolved: sourceRootResolved,
      description: 'external-source',
    });
    if (target !== sourceRootResolved && !target.startsWith(externalSourcesDirResolved + path.sep)) {
      throw new Error(`External-source path escaped review directory: ${entry.path}`);
    }

    const { data: blob } = await octokit.rest.git.getBlob({
      owner: rule.owner,
      repo: rule.repo,
      file_sha: entry.sha,
    });
    if (blob.encoding !== 'base64') {
      noteSkip(`- Skipped unsupported encoding: ${entry.path}`);
      continue;
    }
    const content = Buffer.from(blob.content, 'base64');
    if (content.length > maxSnapshotBytes) {
      noteSkip(`- Skipped oversized file: ${entry.path} (${content.length} bytes)`);
      continue;
    }
    if (!looksLikeText(content)) {
      noteSkip(`- Skipped non-text file: ${entry.path}`);
      continue;
    }
    if (!snapshotBudget.tryReserve(snapshotBudgetCharge(content.length))) {
      budgetSkippedFiles += 1;
      continue;
    }

    fs.mkdirSync(path.dirname(target), { recursive: true });
    fs.writeFileSync(target, content);
    manifest.push(`- Snapshot: ${reviewBundlePath}/external-sources/${rule.repo}/${normalized}`);
    written += 1;
  }

  if (budgetSkippedFiles > 0) {
    manifest.push(
      `- Aggregate snapshot budget: skipped ${budgetSkippedFiles} file(s); `
      + `${snapshotBudget.remainingBytes} of ${snapshotBudget.limitBytes} bytes remained.`,
    );
  }
  if (omittedSkipDetails > 0) {
    manifest.push(`- Skipped-file details: omitted ${omittedSkipDetails} additional entr${omittedSkipDetails === 1 ? 'y' : 'ies'}.`);
  }
  if (written === 0) {
    if (budgetSkippedFiles > 0) {
      manifest.push(`- Status: unavailable because no files fit within the aggregate snapshot budget of ${snapshotBudget.limitBytes} bytes.`);
      return manifest;
    }
    throw new Error(`No text snapshots were written for ${rule.owner}/${rule.repo}@${commit.sha}.`);
  }
  return manifest;
}

function unavailableExternalSourceSection(rule: ExternalSourceRule, message: string): string[] {
  return [
    `## ${rule.owner}/${rule.repo}`,
    '- Status: unavailable',
    `- Reason: ${rule.reason}`,
    `- Error: ${singleLine(message)}`,
    '- Review must continue from the PR diff, changed-file snapshots, current checked-out source, and any available local module references. Missing external dependency snapshots are review input gaps, not a fail-closed condition.',
  ];
}

function externalWatchedFiles(rule: ExternalSourceRule, changedFileTexts: Map<string, string>): Set<string> {
  const watched = new Set(['.github/workflows/go-ci.yml']);
  for (const filename of moduleLockFilenames(rule, changedFileTexts)) {
    watched.add(filename);
    if (filename.endsWith('/go.mod')) {
      watched.add(filename.replace(/go\.mod$/, 'go.sum'));
    } else if (filename === 'go.mod') {
      watched.add('go.sum');
    }
  }
  return watched;
}

function moduleLockFilenames(rule: ExternalSourceRule, changedFileTexts: Map<string, string>): string[] {
  const filenames = new Set(rule.moduleLockFiles);
  for (const filename of changedFileTexts.keys()) {
    if (filename === 'go.mod' || filename.endsWith('/go.mod')) {
      filenames.add(filename);
    }
  }
  return [...filenames].sort();
}

function hasExternalModuleLock({
  rule,
  workspaceDir,
  changedFileTexts,
}: {
  rule: ExternalSourceRule;
  workspaceDir: string;
  changedFileTexts: Map<string, string>;
}): boolean {
  for (const { filename, text } of moduleLockTextsFromWorkspace({ rule, workspaceDir, changedFileTexts })) {
    if (hasExternalGoModReference(rule, filename, text)) {
      return true;
    }
  }
  return false;
}

function moduleLockTextsFromWorkspace({
  rule,
  workspaceDir,
  changedFileTexts,
}: {
  rule: ExternalSourceRule;
  workspaceDir: string;
  changedFileTexts: Map<string, string>;
}): Array<{ filename: string; text: string }> {
  const texts = [];
  for (const filename of moduleLockFilenames(rule, changedFileTexts)) {
    if (changedFileTexts.has(filename)) {
      texts.push({ filename, text: changedFileTexts.get(filename) || '' });
      continue;
    }

    const localPath = path.join(workspaceDir, ...filename.split('/'));
    if (fs.existsSync(localPath)) {
      texts.push({ filename, text: fs.readFileSync(localPath, 'utf8') });
    }
  }
  return texts;
}

function moduleLockTextsFromBase(rule: ExternalSourceRule, workspaceDir: string): Array<{ filename: string; text: string }> {
  return moduleLockTextsFromWorkspace({ rule, workspaceDir, changedFileTexts: new Map() });
}

function pathMatchesPrefix(filename: string, prefix: string): boolean {
  if (prefix.endsWith('/')) {
    return filename.startsWith(prefix);
  }
  return filename === prefix || filename.startsWith(`${prefix}/`);
}

function hasExternalDependencyMarker(rule: ExternalSourceRule, text: string): boolean {
  return text.includes(rule.module) || text.includes(rule.repo);
}

function changedFilePatchHasExternalDependencyMarker(rule: ExternalSourceRule, file: ChangedFile, fallbackText: string): boolean {
  if (typeof file.patch === 'string' && file.patch.trim() !== '') {
    const changedLines = file.patch
      .split(/\r?\n/)
      .filter((line) => (
        (line.startsWith('+') && !line.startsWith('+++')) ||
        (line.startsWith('-') && !line.startsWith('---'))
      ))
      .join('\n');
    return hasExternalDependencyMarker(rule, changedLines);
  }
  return hasExternalDependencyMarker(rule, fallbackText || '');
}

async function resolveExternalRevision({
  rule,
  octokit,
  workspaceDir,
  changedFileTexts,
}: {
  rule: ExternalSourceRule;
  octokit: Octokit;
  workspaceDir: string;
  changedFileTexts: Map<string, string>;
}): Promise<{ kind: 'external'; sha: string } | { kind: 'local-replace' } | { kind: 'missing' }> {
  return resolveExternalRevisionFromModuleLockTexts({
    rule,
    octokit,
    moduleLockTexts: moduleLockTextsFromWorkspace({ rule, workspaceDir, changedFileTexts }),
  });
}

async function resolveExternalRevisionFromWorkspaceBase({
  rule,
  octokit,
  workspaceDir,
}: {
  rule: ExternalSourceRule;
  octokit: Octokit;
  workspaceDir: string;
}): Promise<{ kind: 'external'; sha: string } | { kind: 'local-replace' } | { kind: 'missing' }> {
  return resolveExternalRevisionFromModuleLockTexts({
    rule,
    octokit,
    moduleLockTexts: moduleLockTextsFromBase(rule, workspaceDir),
  });
}

async function resolveExternalRevisionFromModuleLockTexts({
  rule,
  octokit,
  moduleLockTexts,
}: {
  rule: ExternalSourceRule;
  octokit: Octokit;
  moduleLockTexts: Array<{ filename: string; text: string }>;
}): Promise<{ kind: 'external'; sha: string } | { kind: 'local-replace' } | { kind: 'missing' }> {
  const revisions: Array<{ filename: string; source: string; version?: string; sha?: string; localPath?: string }> = [];
  for (const { filename, text } of moduleLockTexts) {
    const resolution = resolveExternalGoModSource(rule, filename, text);
    if (resolution) {
      if (resolution.source === 'local-replace') {
        revisions.push({ filename, source: resolution.source, localPath: resolution.localPath });
        continue;
      }
      const ref = externalModuleVersionRef(resolution.version);
      const { data: commit } = await octokit.rest.repos.getCommit({
        owner: rule.owner,
        repo: rule.repo,
        ref,
      });
      revisions.push({ filename, source: resolution.source, version: resolution.version, sha: commit.sha });
    }
  }

  if (revisions.length === 0) {
    return { kind: 'missing' };
  }
  const sources = new Set(revisions.map((item) => item.source));
  if (sources.has('local-replace')) {
    if (sources.size > 1) {
      throw new Error(`${rule.name} module source shapes disagree: ${revisions.map(formatExternalResolution).join(', ')}`);
    }
    return { kind: 'local-replace' };
  }
  const expected = revisions[0].sha || '';
  for (const revision of revisions.slice(1)) {
    if (revision.sha !== expected) {
      throw new Error(`${rule.name} module locks disagree: ${revisions.map((item) => `${item.filename}@${item.version}=${item.sha}`).join(', ')}`);
    }
  }
  return { kind: 'external', sha: expected };
}

function hasExternalGoModReference(rule: ExternalSourceRule, filename: string, text: string): boolean {
  try {
    const goMod = parseGoModJson(filename, text);
    return (goMod.Require || []).some((item) => item.Path === rule.module) ||
      (goMod.Replace || []).some((item) => item.Old && item.Old.Path === rule.module);
  } catch {
    return hasExternalDependencyMarker(rule, text);
  }
}

function resolveExternalGoModSource(rule: ExternalSourceRule, filename: string, text: string): null | {
  version: string;
  source: 'require' | 'replace';
} | {
  source: 'local-replace';
  localPath: string;
} {
  const goMod = parseGoModJson(filename, text);
  const requirement = singleExternalRequirement(rule, filename, goMod);
  if (!requirement) {
    return null;
  }

  const replacements = (goMod.Replace || []).filter((item) => {
    if (!item.Old || item.Old.Path !== rule.module) {
      return false;
    }
    return !item.Old.Version || item.Old.Version === requirement.Version;
  });
  if (replacements.length > 1) {
    throw new Error(`${filename} has multiple applicable replace directives for ${rule.module}; cannot resolve a single authoritative source.`);
  }
  if (replacements.length === 0) {
    return { version: requirement.Version, source: 'require' };
  }

  const replacement = replacements[0];
  const replacementTarget = replacement.New && replacement.New.Path ? replacement.New.Path : '';
  const replacementVersion = replacement.New && replacement.New.Version ? replacement.New.Version : '';
  if (replacementTarget !== rule.module) {
    if (isLocalModuleReplacePath(replacementTarget)) {
      return { source: 'local-replace', localPath: replacementTarget };
    }
    throw new Error(`${filename} replaces ${rule.module} with ${replacementTarget || '(empty target)'}; external source snapshots can only represent ${rule.owner}/${rule.repo}.`);
  }
  if (!replacementVersion) {
    throw new Error(`${filename} replaces ${rule.module} with ${replacementTarget} without a version; cannot resolve a pinned external source revision.`);
  }
  return { version: replacementVersion, source: 'replace' };
}

function parseGoModJson(_filename: string, text: string): {
  Require?: Array<{ Path: string; Version: string }>;
  Replace?: Array<{ Old?: { Path: string; Version?: string }; New?: { Path?: string; Version?: string } }>;
} {
  const result: {
    Require: Array<{ Path: string; Version: string }>;
    Replace: Array<{ Old?: { Path: string; Version?: string }; New?: { Path?: string; Version?: string } }>;
  } = { Require: [], Replace: [] };
  let mode: 'require' | 'replace' | null = null;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = stripGoModComment(rawLine).trim();
    if (line === '') {
      continue;
    }
    if (line === ')') {
      mode = null;
      continue;
    }
    if (line === 'require (') {
      mode = 'require';
      continue;
    }
    if (line === 'replace (') {
      mode = 'replace';
      continue;
    }
    if (line.startsWith('require ')) {
      parseRequireLine(line.slice('require '.length), result.Require);
      continue;
    }
    if (line.startsWith('replace ')) {
      parseReplaceLine(line.slice('replace '.length), result.Replace);
      continue;
    }
    if (mode === 'require') {
      parseRequireLine(line, result.Require);
      continue;
    }
    if (mode === 'replace') {
      parseReplaceLine(line, result.Replace);
    }
  }

  return result;
}

function parseRequireLine(line: string, requirements: Array<{ Path: string; Version: string }>): void {
  const tokens = goModTokens(line);
  if (tokens.length >= 2) {
    requirements.push({ Path: tokens[0], Version: tokens[1] });
  }
}

function parseReplaceLine(
  line: string,
  replacements: Array<{ Old?: { Path: string; Version?: string }; New?: { Path?: string; Version?: string } }>,
): void {
  const arrowIndex = line.indexOf('=>');
  if (arrowIndex < 0) {
    return;
  }
  const oldTokens = goModTokens(line.slice(0, arrowIndex));
  const newTokens = goModTokens(line.slice(arrowIndex + 2));
  if (oldTokens.length < 1 || newTokens.length < 1) {
    return;
  }
  replacements.push({
    Old: {
      Path: oldTokens[0],
      Version: oldTokens[1],
    },
    New: {
      Path: newTokens[0],
      Version: newTokens[1],
    },
  });
}

function stripGoModComment(line: string): string {
  let quote: '"' | '`' | null = null;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (quote === '"') {
      if (char === '\\') {
        i += 1;
      } else if (char === '"') {
        quote = null;
      }
      continue;
    }
    if (quote === '`') {
      if (char === '`') {
        quote = null;
      }
      continue;
    }
    if (char === '"' || char === '`') {
      quote = char;
      continue;
    }
    if (char === '/' && line[i + 1] === '/') {
      return line.slice(0, i);
    }
  }
  return line;
}

function goModTokens(line: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let quote: '"' | '`' | null = null;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (quote === '"') {
      if (char === '\\') {
        const next = line[i + 1];
        if (next) {
          current += next;
          i += 1;
        }
      } else if (char === '"') {
        quote = null;
      } else {
        current += char;
      }
      continue;
    }
    if (quote === '`') {
      if (char === '`') {
        quote = null;
      } else {
        current += char;
      }
      continue;
    }
    if (char === '"' || char === '`') {
      quote = char;
      continue;
    }
    if (/\s/.test(char)) {
      if (current !== '') {
        tokens.push(current);
        current = '';
      }
      continue;
    }
    current += char;
  }
  if (current !== '') {
    tokens.push(current);
  }
  return tokens;
}

function singleExternalRequirement(
  rule: ExternalSourceRule,
  filename: string,
  goMod: { Require?: Array<{ Path: string; Version: string }> },
): { Path: string; Version: string } | null {
  const requirements = (goMod.Require || []).filter((item) => item.Path === rule.module);
  if (requirements.length > 1) {
    throw new Error(`${filename} has multiple require directives for ${rule.module}; cannot resolve a single authoritative source.`);
  }
  if (requirements.length === 0) {
    return null;
  }
  if (!requirements[0].Version) {
    throw new Error(`${filename} requires ${rule.module} without a version.`);
  }
  return requirements[0];
}

function isLocalModuleReplacePath(target: string): boolean {
  return target === '.' ||
    target === '..' ||
    target.startsWith('./') ||
    target.startsWith('../') ||
    target.startsWith('/');
}

export function externalModuleVersionRef(version: string): string {
  const match = version.match(/^v[^\s]*-\d{14}-([0-9a-f]{12})$/);
  return match ? match[1] : version;
}

function formatExternalResolution(item: { filename: string; source: string; version?: string; sha?: string; localPath?: string }): string {
  if (item.source === 'local-replace') {
    return `${item.filename}@local:${item.localPath}`;
  }
  return `${item.filename}@${item.version}=${item.sha}`;
}

function singleLine(value: string): string {
  return String(value || '').replace(/\s+/g, ' ').trim() || '(unknown)';
}
